import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Form, Spinner } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import { LuGraduationCap } from 'react-icons/lu'
import { BsArrowClockwise, BsArrowRight, BsSearch } from 'react-icons/bs'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import { getValidInstituteIdFromCookie } from '../utils/instituteIdCookie'

const FIELD_LABELS = {
  payment_method: 'Payment Method',
  transaction_amount: 'Transaction Amount',
  cash: 'Cash',
  online: 'Online',
  cheque: 'Cheque',
  transaction_date: 'Transaction Date',
  transaction_id: 'Transaction ID',
  discount: 'Discount',
  amount: 'Amount',
  fees_status: 'Fee Status',
  fees_name: 'Fee Name',
}

const formatDisplayDateTime = (dateStr) => {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return dateStr
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—'
  const moneyKeys = ['transaction_amount', 'cash', 'online', 'cheque', 'discount', 'amount']
  if (moneyKeys.includes(key)) {
    const num = Number(value)
    if (!Number.isNaN(num)) return `₹ ${num}`
  }
  if (key === 'payment_method' || key === 'fees_status') {
    return String(value).replace(/_/g, ' ')
  }
  return String(value)
}

const getChangedFields = (oldValues = {}, newValues = {}) => {
  const keys = Array.from(
    new Set([...Object.keys(oldValues || {}), ...Object.keys(newValues || {})]),
  )
  return keys.map((key) => ({
    key,
    label: FIELD_LABELS[key] || key.replace(/_/g, ' '),
    oldValue: oldValues?.[key],
    newValue: newValues?.[key],
  }))
}

const extractLogs = (response) => {
  const body = response?.data
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body)) return body
  return []
}

const getApiErrorMessage = (error, fallback) => {
  const status = error?.response?.status
  if (status === 401) return 'Your session expired. Please log in again.'
  if (status === 403) return 'You are not authorized to view fee update logs.'
  const data = error?.response?.data
  if (data?.message) return data.message
  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat()[0]
    if (firstError) return firstError
  }
  return fallback
}

const logSearchText = (log) =>
  [
    log?.student?.name,
    log?.student?.admission_number,
    log?.student_id,
    log?.fee?.fees_name,
    log?.fee_id,
    log?.changed_by?.name,
    log?.changed_by?.user_type,
    log?.action,
    log?.new_values?.transaction_id,
    log?.old_values?.transaction_id,
  ]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .join(' ')
    .toLowerCase()

export default function FeeUpdateLogs() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo, id: instituteId } = useInstitute()
  const navigate = useNavigate()

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const fetchLogs = useCallback(async () => {
    const resolvedInstituteId = getValidInstituteIdFromCookie() || Number(instituteId)
    if (!Number.isFinite(resolvedInstituteId) || resolvedInstituteId <= 0) {
      toast.error('Institute ID is missing. Please select an institute.')
      setLogs([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await ApiEndpoints.getFeeChangeLogs()
      if (response?.data?.status === false) {
        toast.error(response.data.message || 'Failed to load fee update logs.')
        setLogs([])
        return
      }
      setLogs(extractLogs(response))
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load fee update logs.'))
      setLogs([])
    } finally {
      setLoading(false)
    }
  }, [instituteId])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const filteredLogs = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return logs
    return logs.filter((log) => logSearchText(log).includes(query))
  }, [logs, searchTerm])

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: 12,
          paddingRight: 12,
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                padding: '12px 28px',
                borderBottom: '1px solid #D1D3D4',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Fee Update Logs
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '24px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: '#111827',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                All fee payment updates
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#6B7280',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Newest first · {filteredLogs.length} of {logs.length} log
                {logs.length === 1 ? '' : 's'}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <div style={{ position: 'relative', minWidth: isMobile ? '100%' : 260 }}>
                <BsSearch
                  size={14}
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6B7280',
                  }}
                />
                <Form.Control
                  type="search"
                  placeholder="Search student, admission no, fee, or user"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    height: 42,
                    paddingLeft: 34,
                    borderRadius: 8,
                    border: '1px solid #D1D3D4',
                    fontSize: 13,
                  }}
                />
              </div>
              <button
                type="button"
                onClick={fetchLogs}
                disabled={loading}
                style={{
                  height: 42,
                  borderRadius: 8,
                  backgroundColor: '#223843',
                  border: 'none',
                  color: '#ffffff',
                  padding: '0 16px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Inter, system-ui, sans-serif',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  // marginLeft: isMobile ? 'auto' : 'unset',
                }}
              >
                <BsArrowClockwise size={16} />
                {/* Refresh */}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" style={{ color: '#223843' }} />
            </div>
          ) : filteredLogs.length === 0 ? (
            <Card
              className="border-0"
              style={{
                borderRadius: 12,
                border: '1px solid #D1D3D4',
              }}
            >
              <Card.Body className="text-center py-5">
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#111827',
                    marginBottom: 6,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {logs.length === 0 ? 'No fee update logs yet' : 'No matching logs'}
                </div>
                <div style={{ fontSize: 13, color: '#6B7280' }}>
                  {logs.length === 0
                    ? 'When a fee payment is edited, the before and after values will appear here.'
                    : 'Try a different student name, admission number, or fee name.'}
                </div>
              </Card.Body>
            </Card>
          ) : (
            <div className="d-flex flex-column gap-3">
              {filteredLogs.map((log, index) => {
                const changedFields = getChangedFields(log.old_values, log.new_values)
                const logKey = log.id || `${log.fee_id}-${log.created_at}-${index}`
                return (
                  <Card
                    key={logKey}
                    className="border-0"
                    style={{
                      borderRadius: 12,
                      border: '1px solid #D1D3D4',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        background: '#F9FAFB',
                        borderBottom: '1px solid #E5E7EB',
                        padding: '14px 18px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: 10,
                        }}
                      >
                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <span
                              style={{
                                background: '#223843',
                                color: '#fff',
                                borderRadius: 20,
                                padding: '3px 10px',
                                fontSize: 12,
                                fontWeight: 700,
                                textTransform: 'capitalize',
                              }}
                            >
                              {log.action || 'update'}
                            </span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                              {log.student?.name || 'Unknown student'}
                            </span>
                          </div>
                          <div style={{ fontSize: 13, color: '#6B7280' }}>
                            {log.student?.admission_number || 'No admission no.'}
                            {' · '}
                            {log.fee?.fees_name || 'Fee'}
                            {log.fee?.amount != null ? ` · ₹ ${log.fee.amount}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                          <div style={{ fontSize: 13, color: '#6B7280' }}>
                            {formatDisplayDateTime(log.created_at)}
                          </div>
                          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                            by {log.changed_by?.name || '—'}
                            {log.changed_by?.user_type
                              ? ` (${log.changed_by.user_type})`
                              : ''}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Card.Body style={{ padding: 18 }}>
                        {/* <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                            gap: 12,
                            marginBottom: 16,
                          }}
                        >
                          <div
                            style={{
                              background: '#F9FAFB',
                              borderRadius: 8,
                              padding: 12,
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                              Changed By
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                              {log.changed_by?.name || '—'}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#6B7280',
                                textTransform: 'capitalize',
                                marginTop: 2,
                              }}
                            >
                              {log.changed_by?.user_type || '—'}
                            </div>
                          </div>

                          <div
                            style={{
                              background: '#F9FAFB',
                              borderRadius: 8,
                              padding: 12,
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                              Student
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                              {log.student?.name || '—'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                              {log.student?.admission_number || '—'}
                            </div>
                            {log.student_id ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/student/profile/${log.student_id}`)}
                                style={{
                                  marginTop: 8,
                                  border: 'none',
                                  background: 'transparent',
                                  padding: 0,
                                  color: '#223843',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textDecoration: 'underline',
                                }}
                              >
                                View profile
                              </button>
                            ) : null}
                          </div>

                          <div
                            style={{
                              background: '#F9FAFB',
                              borderRadius: 8,
                              padding: 12,
                              border: '1px solid #E5E7EB',
                            }}
                          >
                            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                              Fee
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                              {log.fee?.fees_name || '—'}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: '#6B7280',
                                marginTop: 2,
                                textTransform: 'capitalize',
                              }}
                            >
                              ₹ {log.fee?.amount ?? '—'} · {log.fee?.fees_status || '—'}
                            </div>
                            {log.fee_id ? (
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/FeeChangeLogs/${log.fee_id}`, {
                                    state: {
                                      studentName: log.student?.name,
                                      admissionNumber: log.student?.admission_number,
                                      feeName: log.fee?.fees_name,
                                      feeAmount: log.fee?.amount,
                                      feeStatus: log.fee?.fees_status,
                                    },
                                  })
                                }
                                style={{
                                  marginTop: 8,
                                  border: 'none',
                                  background: 'transparent',
                                  padding: 0,
                                  color: '#223843',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textDecoration: 'underline',
                                }}
                              >
                                View this fee’s logs
                              </button>
                            ) : null}
                          </div>
                        </div> */}

                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#111827',
                            marginBottom: 10,
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Changed Fields
                        </div>

                        {changedFields.length === 0 ? (
                          <div style={{ fontSize: 13, color: '#6B7280' }}>
                            No field-level changes recorded.
                          </div>
                        ) : (
                          <div className="d-flex flex-column gap-2">
                            {changedFields.map((field) => (
                              <div
                                key={`${logKey}-${field.key}`}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: isMobile
                                    ? '1fr'
                                    : '160px 1fr auto 1fr',
                                  gap: 10,
                                  alignItems: 'center',
                                  padding: '10px 12px',
                                  borderRadius: 8,
                                  border: '1px solid #E5E7EB',
                                  background: '#ffffff',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#374151',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {field.label}
                                </div>
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: '#991B1B',
                                    background: '#FEF2F2',
                                    borderRadius: 6,
                                    padding: '8px 10px',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>
                                    BEFORE
                                  </span>
                                  <div>{formatFieldValue(field.key, field.oldValue)}</div>
                                </div>
                                {!isMobile && (
                                  <BsArrowRight size={16} style={{ color: '#9CA3AF' }} />
                                )}
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: '#065F46',
                                    background: '#ECFDF5',
                                    borderRadius: 6,
                                    padding: '8px 10px',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>
                                    AFTER
                                  </span>
                                  <div>{formatFieldValue(field.key, field.newValue)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card.Body>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
